import {debounce, isNullOrEmpty} from "@2d-front-monorepo/core";
import {Loader} from '@googlemaps/js-api-loader';
// import pkg from '@googlemaps/js-api-loader';
// const { Loader } = pkg;
import {JSX, ReactNode, useCallback, useEffect, useId, useRef, useState} from "react";
import {MaterialIcon} from "../index";
import ListBox, {ListBoxItemProps} from "../ListBox/ListBox";
import {CircleLiteral, LatLngBoundsLiteral, LatLngLiteral} from "./GoogleMapsTypes";

export type GoogleAddressComponentProps = {
	apiKey: string;
	onSelect: (item: PlaceDetails) => void;
	label: ReactNode,
	placeholder?: string;
	autoFocus?: boolean;

	language?: string | undefined;
	locationBias?: LatLngLiteral | LatLngBoundsLiteral | CircleLiteral;
	locationRestriction?: LatLngBoundsLiteral;
	region?: string | undefined;
}

let placesLibrary: google.maps.PlacesLibrary | undefined = undefined;
let executedLoad: boolean = false;

export type PlaceDetails = {
	addressComponents: AddressComponent[];
	formattedAddress: string;
}

export type AddressComponent = {
	longText: string | null;
	shortText: string | null;
	types: string[];
}

export default function GoogleAddressComponent({
	                                               apiKey,
	                                               onSelect,
	                                               label,
	                                               placeholder,
	                                               autoFocus,
	                                               language,
	                                               locationBias,
	                                               locationRestriction,
	                                               region
                                               }: GoogleAddressComponentProps): JSX.Element {
	const id = useId();
	const inputRef = useRef<HTMLInputElement>(null);
	const [firstTimeLoad, setFirstTimeLoad] = useState<boolean>(placesLibrary == null);
	const [loading, setLoading] = useState<boolean>(false);
	const [listBoxItems, setListBoxItems] = useState<ListBoxItemProps<google.maps.places.Place>[]>([]);
	const [listBoxVisible, setListBoxVisible] = useState<boolean>(false);

	const selectedCallback = useCallback((value: google.maps.places.Place): void => {
		setLoading(false);
		setListBoxVisible(false);

		if (value == null) {
			return;
		}

		if (inputRef.current != null) {
			inputRef.current.value = value.formattedAddress!;
		}

		onSelect({
			addressComponents: value.addressComponents!,
			formattedAddress: value.formattedAddress!
		});

	}, [onSelect, setLoading, setListBoxVisible]);

	const fetch = useCallback(debounce((pr: google.maps.places.SearchByTextRequest) => {
			if (!placesLibrary) return;
			setLoading(true);
			setListBoxVisible(false);
			placesLibrary.Place.searchByText(pr).then(p => {
				setLoading(false);
				setListBoxVisible(true);

				const places = p.places;
				if (places.length === 0) {
					setListBoxItems([{label: "No results found", value: null!}]);
				} else {
					setListBoxItems(places.filter(x => x.formattedAddress != null).map((item => ({label: item.formattedAddress!, value: item}))));
				}
			})
		}, 350), []
	);

	const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;

		if (isNullOrEmpty(value)) {
			setListBoxVisible(false);
			setLoading(false);

		} else {
			setLoading(true);
			fetch({
				textQuery: value,
				fields: ["addressComponents", "formattedAddress"],
				language: language,
				locationBias: locationBias,
				locationRestriction: locationRestriction,
				region: region
			})
		}
	}, [setListBoxVisible, setLoading, fetch]);

	useEffect(() => {
		if (firstTimeLoad && !executedLoad) {
			executedLoad = true;
			new Loader({apiKey: apiKey}).importLibrary("places").then((res) => {
				placesLibrary = res as google.maps.PlacesLibrary;
				setFirstTimeLoad(false);
			}).catch(e => {
				inputRef.current && (inputRef.current.value = e.toString())
			});
		}
	}, []);

	return (
		<div
			className={"labelled-field google-address" + (firstTimeLoad ? " google-address--loading" : "")}>
			<div className={"form__field form__field--search"}>
				<label className="field__label" htmlFor={id}>
					<span className="label__text">{label}</span>
				</label>
				<span className="field__input">
					<input type="search"
					       id={id}
					       ref={inputRef}
					       disabled={firstTimeLoad}
					       onInput={onInputChange}
					       placeholder={placeholder}
					       autoFocus={autoFocus} />
					{loading && <span className="google-address__search--loading"><MaterialIcon icon="progress_activity"/></span>}
                </span>
				{listBoxVisible && <ListBox items={listBoxItems} onSelect={selectedCallback}/>}
			</div>
		</div>);
};
